import React, { useState } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { userAPI } from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const ForwardDialog = ({ open, onClose, message, channels, users, currentUser, onForward }) => {
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [forwarding, setForwarding] = useState(false);

  const handleForward = async () => {
    if (selectedChannels.length === 0) {
      toast.error('Please select at least one chat');
      return;
    }

    setForwarding(true);
    try {
      await onForward(message.id, selectedChannels);
      toast.success(`Message forwarded to ${selectedChannels.length} chat(s)`);
      setSelectedChannels([]);
      onClose();
    } catch (error) {
      toast.error('Failed to forward message');
    } finally {
      setForwarding(false);
    }
  };

  const getChannelDisplay = (channel) => {
    if (channel.channel_type === 'direct') {
      const otherParticipantId = channel.participants.find(p => p !== currentUser?.id);
      const detail = channel.participant_details?.find(p => p.id === otherParticipantId);
      const user = detail || users.find(u => u.id === otherParticipantId);
      return {
        name: user?.name || 'Unknown User',
        avatar: user?.name?.charAt(0).toUpperCase() || '?',
        avatarUrl: user?.avatar_url ? userAPI.getAvatarUrl(user.avatar_url) : null,
      };
    } else {
      return {
        name: channel.name || 'Group Chat',
        avatar: 'G',
        avatarUrl: null,
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle>Forward Message</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-2">
            {channels.map((channel) => {
              const display = getChannelDisplay(channel);
              const isSelected = selectedChannels.includes(channel.id);

              return (
                <div
                  key={channel.id}
                  className="flex items-center gap-3 p-3 hover:bg-[#F1F5F9] dark:hover:bg-[#1E293B] rounded-md transition-colors cursor-pointer"
                  onClick={() => {
                    if (isSelected) {
                      setSelectedChannels(selectedChannels.filter(id => id !== channel.id));
                    } else {
                      setSelectedChannels([...selectedChannels, channel.id]);
                    }
                  }}
                  data-testid={`forward-channel-${channel.id}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => {}}
                  />
                  <Avatar>
                    <AvatarImage src={display.avatarUrl} />
                    <AvatarFallback className="bg-[#4338CA] text-white">
                      {display.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{display.name}</span>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleForward}
            disabled={forwarding || selectedChannels.length === 0}
            className="bg-[#4338CA] hover:bg-[#3730A3]"
            data-testid="forward-confirm-button"
          >
            {forwarding ? 'Forwarding...' : `Forward to ${selectedChannels.length} chat(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardDialog;
